import React, { useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  Stack,
  Input,
  FormControl,
  FormLabel,
} from '@mui/joy';
import { Envelope as Email, ArrowsClockwise as Refresh } from '@phosphor-icons/react';

export const SignupSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [resendEmail, setResendEmail] = useState(email);
  const [resendStatus, setResendStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');

  const handleResendVerification = async () => {
    if (!resendEmail) return;

    setResendStatus('loading');
    try {
      const response = await fetch('/api/signup/resend-verification/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resendEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendStatus('success');
        setResendMessage(data.message || 'Verification email sent!');
      } else if (response.status === 429) {
        setResendStatus('error');
        setResendMessage(data.error || 'Please wait before requesting another email.');
      } else {
        setResendStatus('error');
        setResendMessage(data.error || 'Failed to resend verification email.');
      }
    } catch {
      setResendStatus('error');
      setResendMessage('An error occurred. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.level1',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
            <Email size={64} color="var(--joy-palette-primary-500)" />

            <Typography level="h3" textAlign="center">
              Check Your Email
            </Typography>

            <Alert color="success" sx={{ width: '100%', textAlign: 'center' }}>
              <Typography level="body-md">
                We've sent a verification link to <strong>{email || 'your email address'}</strong>. Please check your inbox and click the link to activate your account.
              </Typography>
            </Alert>

            <Typography level="body-md" textAlign="center" color="neutral">
              The verification link will expire in 24 hours.
            </Typography>

            <Box sx={{ width: '100%', pt: 2 }}>
              <Typography level="body-sm" textAlign="center" sx={{ mb: 2 }}>
                Didn't receive the email? Check your spam folder or request a new link:
              </Typography>

              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Email Address</FormLabel>
                  <Input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                </FormControl>

                <Button
                  onClick={handleResendVerification}
                  loading={resendStatus === 'loading'}
                  startDecorator={<Refresh />}
                  variant="soft"
                  color="primary"
                  fullWidth
                >
                  Resend Verification Email
                </Button>

                {resendStatus === 'success' && (
                  <Alert color="success" size="sm">
                    {resendMessage}
                  </Alert>
                )}

                {resendStatus === 'error' && (
                  <Alert color="danger" size="sm">
                    {resendMessage}
                  </Alert>
                )}
              </Stack>
            </Box>

            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              color="neutral"
              sx={{ mt: 2 }}
            >
              Back to Login
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
